'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect } from 'react';
import { useDownloadEmVoo } from '@/shared/hooks/useDownloadEmVoo';
import { useToast } from '@/shared/providers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { baixarPdfCertificadoAcessos } from '../store/certificados/actions';
import { selectCertificadoAcessosPdfBaixando } from '../store/certificados/selectors';
import type { OrigemPdfCertificadoAcessos } from '../types/certificadoAcessosTypes';
interface UseBaixarPdfCertificadoAcessosInput {
  processoId: string;
  origem: OrigemPdfCertificadoAcessos;
  /** `null` enquanto o certificado não carregou: sem ele não há nome de arquivo, e o clique não faz nada. */
  numeroProcesso: string | null;
}

/**
 * O "Baixar PDF" do Certificado de Acessos em qualquer porta: o arquivo vem do servidor, nunca da
 * tela.
 *
 * A versão anterior rasterizava o DOM (`domToCanvas` + `jsPDF`) e entregava como peça de prova
 * um documento que nunca existiu no servidor: o `/autenticar` calcula o sha256 do arquivo
 * enviado e compara com o `hashArquivo` gravado na emissão, então o próprio sistema reprovava
 * o arquivo que ele mesmo entregava — além de ser imagem, sem texto pesquisável.
 *
 * `abortarDownload` é exposto para quem limpa a fatia ao sair: tem de abortar ANTES de
 * `clearCertificadoAcessos`, que solta a trava do download — com a cadeia antiga ainda viva, um
 * novo clique abria uma segunda ao lado dela, e a órfã salvava o arquivo depois de o usuário ter
 * saído (review do #1160, CodeRabbit).
 */
export function useBaixarPdfCertificadoAcessos(t0) {
  const $ = _c(18);
  const {
    processoId,
    origem,
    numeroProcesso
  } = t0;
  const dispatch = useAppDispatch();
  const {
    showError,
    showSuccess
  } = useToast();
  const isDownloadingPdf = useAppSelector(selectCertificadoAcessosPdfBaixando);
  const {
    baixar,
    abortar
  } = useDownloadEmVoo(baixarPdfCertificadoAcessos.rejected.match);
  let t1;
  if ($[0] !== abortar) {
    t1 = () => abortar;
    $[0] = abortar;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== abortar || $[3] !== origem || $[4] !== processoId) {
    t2 = [abortar, processoId, origem];
    $[2] = abortar;
    $[3] = origem;
    $[4] = processoId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== baixar || $[7] !== dispatch || $[8] !== numeroProcesso || $[9] !== origem || $[10] !== processoId || $[11] !== showError || $[12] !== showSuccess) {
    t3 = async () => {
      if (!numeroProcesso) {
        return;
      }
      const desfecho = await baixar(() => dispatch(baixarPdfCertificadoAcessos({
        processoId,
        origem,
        nomeArquivo: `certificado-acessos-${numeroProcesso.toLowerCase()}.pdf`
      })));
      if (desfecho.tipo === "ignorado") {
        return;
      }
      if (desfecho.tipo === "falhou") {
        showError("Erro ao gerar PDF", desfecho.erro ?? "Falha ao gerar o PDF");
        return;
      }
      showSuccess("PDF gerado com sucesso");
    };
    $[6] = baixar;
    $[7] = dispatch;
    $[8] = numeroProcesso;
    $[9] = origem;
    $[10] = processoId;
    $[11] = showError;
    $[12] = showSuccess;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const baixarPdf = t3;
  let t4;
  if ($[14] !== abortar || $[15] !== baixarPdf || $[16] !== isDownloadingPdf) {
    t4 = {
      isDownloadingPdf,
      baixarPdf,
      abortarDownload: abortar
    };
    $[14] = abortar;
    $[15] = baixarPdf;
    $[16] = isDownloadingPdf;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}